import React from "react";
import { createRoot } from "react-dom/client";
import PageHeaderChrome from "./pageHeaders/PageHeaderChrome";

// Bridge that lets a legacy v1 page (React 18, separate bundle) render a v2
// React 19 page header. v1 cannot import v2 modules, so the seam is a window
// global plus DOM nodes - the same isolation the chrome navbar/footer roots use.
// Only plain data + callbacks cross it (see the header definitions); no React
// element or context ever does.
//
// One React root per host element, cached in a WeakMap so a re-mount of the same
// element is a re-render (React reconciles in place - a term change updates the
// header with no flicker and no leaked root). The host element is owned by the
// v1 V2PageHeader component, which calls unmount on cleanup when its route leaves.
const roots = new WeakMap();

// The header host is sticky at the top of the window, so it covers the first N
// pixels of whatever the legacy page pins below it. Legacy pages have their own
// sticky columns (the student/staff list drawers, the form builder's field and
// properties columns) that need to pin *under* the header rather than behind it,
// and N is not a constant - each header definition renders its own content, and
// the same header changes height when it wraps on a narrow window.
//
// So publish the measured height as a custom property on <html> and let those
// styles offset against it (`top: var(--v1-page-header-height, 0px)`). The
// fallback covers legacy pages that mount no header at all.
const HEADER_HEIGHT_VAR = "--v1-page-header-height";
const observers = new WeakMap();

function publishHeight(el) {
  document.documentElement.style.setProperty(
    HEADER_HEIGHT_VAR,
    `${Math.round(el.getBoundingClientRect().height)}px`
  );
}

function observeHeight(el) {
  if (observers.has(el) || typeof ResizeObserver === "undefined") return;
  const observer = new ResizeObserver(() => publishHeight(el));
  observer.observe(el);
  observers.set(el, observer);
}

function mountPageHeader(el, headerKey, params) {
  if (!el) return;
  let root = roots.get(el);
  if (!root) {
    root = createRoot(el);
    roots.set(el, root);
  }
  root.render(<PageHeaderChrome headerKey={headerKey} params={params} />);
  observeHeight(el);
  publishHeight(el);
}

function unmountPageHeader(el) {
  const root = el && roots.get(el);
  if (!root) return;
  root.unmount();
  roots.delete(el);
  observers.get(el)?.disconnect();
  observers.delete(el);
  document.documentElement.style.setProperty(HEADER_HEIGHT_VAR, "0px");
}

// Namespaced merge so other chrome surfaces can attach their own helpers here.
window.AeChrome = {
  ...(window.AeChrome || {}),
  mountPageHeader,
  unmountPageHeader,
};

// The chrome bundle is a separate script from the v1 SPA and can evaluate after
// a v1 page has already mounted (notably in dev, where this is a Vite module
// graph fetched over the network). A v1 V2PageHeader that mounted before this
// ran found no window.AeChrome; signal readiness so it can mount now instead of
// waiting for an unrelated re-render that may never come.
window.dispatchEvent(new Event("aechrome:ready"));
